import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { EmailLink } from "./PolicyLayout";

function Returns() {
  return (
    <PolicyLayout
      title="returns & refund policy"
      intro="This policy covers voluntary returns, statutory withdrawal rights, damaged goods, exchanges, and refunds."
    >
      <div className="policy-callout">
        <p>
          Our voluntary return period is 14 days after delivery. Any longer or additional mandatory
          right provided by the law where you live remains available.
        </p>
      </div>

      <h2>1. Voluntary returns</h2>
      <p>
        You may request a return within 14 days after delivery. Unless an item is defective, incorrect,
        or covered by a mandatory withdrawal right, it must be unused, unmarked, complete, and in its
        original packaging with any accessories. A brief inspection comparable to what is reasonably
        possible in a shop does not by itself make a return ineligible where law protects that inspection.
      </p>

      <h2>2. How to start a return</h2>
      <ol>
        <li>Email <EmailLink subject="Return request" /> with the order number, purchaser name, item, quantity, and reason.</li>
        <li>Wait for return instructions and the return destination before sending the parcel.</li>
        <li>Package the item securely and include the information requested in our instructions.</li>
        <li>Use a tracked service and keep proof of shipment until the return is complete.</li>
      </ol>
      <p>Sending a parcel without contacting us may delay identification and processing.</p>

      <h2>3. Damaged, defective, or incorrect items</h2>
      <p>
        Inspect the order after delivery. Contact us as soon as reasonably possible if an item is
        damaged, defective, or different from what you ordered. Include clear photographs of the item,
        packaging, and shipping label if available. We will assess the issue and provide the remedy
        required by law, which may include repair, replacement, price reduction, or refund. We cover
        reasonable return costs when the item is confirmed as faulty or incorrectly supplied.
      </p>

      <h2>4. Items that may not qualify for a change-of-mind return</h2>
      <ul>
        <li>personalized or made-to-order goods once production has begun;</li>
        <li>sealed goods that are unsuitable for return for genuine health or hygiene reasons after the seal is broken;</li>
        <li>ink or other consumables that have been opened, used, contaminated, or materially depleted;</li>
        <li>gift cards or other stored-value products, where non-returnability is lawful; and</li>
        <li>items damaged by misuse, neglect, modification, or failure to follow care instructions.</li>
      </ul>
      <p>
        An exception never removes a right relating to a defective, misdescribed, or unlawfully supplied
        product. Sale items are eligible for the same defect remedies and any mandatory return rights.
      </p>

      <h2>5. Statutory withdrawal rights</h2>
      <p>
        Some locations give consumers a cooling-off or withdrawal right for online purchases. If it
        applies, you may notify us within the statutory period using any clear statement, including an
        email with your order number and a statement that you withdraw from the purchase. We will
        provide the instructions and refunds required by that law. Lawful exceptions may apply to
        personalized or unsealed protected goods.
      </p>

      <h2>6. Return shipping</h2>
      <p>
        For a voluntary change-of-mind return, you are responsible for return postage unless applicable
        law says otherwise. Original expedited delivery upgrades may not be refundable beyond the cost
        of the least expensive standard delivery option when the law permits. Do not send a return
        postage-due. Customs declarations for an international return should clearly identify returned goods.
      </p>

      <h2>7. Refunds</h2>
      <p>
        We will inspect the return and notify you of the outcome. Approved refunds are sent to the
        original payment method unless the law requires or you expressly agree to another method. We
        aim to issue an approved refund within 5 business days after inspection; banks and payment
        providers may need additional time to post it. Any statutory refund deadline takes priority.
      </p>

      <h2>8. Exchanges</h2>
      <p>
        Direct exchanges depend on stock and destination. The quickest method may be to return an
        eligible item and place a separate order. We will not charge you again for a replacement of a
        confirmed faulty or incorrectly supplied product where the law requires replacement.
      </p>

      <h2>9. Lost return parcels</h2>
      <p>
        For change-of-mind returns, responsibility generally remains with you until the return reaches
        the designated address, subject to applicable law. We cannot process a parcel we do not receive,
        so tracked postage and proof of shipment are recommended.
      </p>

      <h2>10. Related policies</h2>
      <p>
        Read our <Link to="/orders">Order &amp; Cancellation Policy</Link>, <Link to="/shipping">Shipping &amp; Delivery Policy</Link>,
        and <Link to="/warranty"> Product Warranty Policy</Link>. Contact <EmailLink subject="Return or refund question" /> for help.
      </p>
    </PolicyLayout>
  );
}

export default Returns;
